/**
 * useTutorApplicationVideoUploadAdapter — l'envoi de la vidéo d'une question de la candidature,
 * par l'orchestration Vapor de l'ancien `InterviewQuestion` (exception n° 2 de `ports/CLAUDE.md`) :
 * le fichier part en `tmp/` (formulaire `APPLICATION_FORM`), puis
 * `POST /api/v1/app-questions/{id}/submit-answer` avec `video: [<clé tmp>]` ; le réducteur range la
 * réponse (`video_tmp:…`) dans `state.data.tutors.testAnswers`.
 *
 * Le fichier : au web, la vidéo telle que l'enregistreur la pose (`{ uri }` d'un `blob:`) ; au
 * téléphone, `{ uri, name, type: "video/mp4" }`, nommé `Masteur_Video_<nom>_<date>.mp4`.
 *
 * Jamais `showErrorDialog` : un échec est mesuré (`gesture.failed`) et l'écran reste sur la question,
 * comme l'ancien, qui n'affichait rien.
 */
import { Platform } from "react-native"

import { useRequestsBatch } from "../../../../common/hooks/useRequestsBatch"
import { tutorsService } from "../../../../data/services/tutorsService"
import { applicationVideoFileName } from "../../adapters/tutor/Application/tutorVideoQuestionAdapter"
import { useGesture } from "./gestureTracking"

export type ApplicationVideoRecord = { uri?: string | null } | null | undefined

export function useTutorApplicationVideoUploadAdapter(args: {
    questionId: string | number | null | undefined
    videoRecord: ApplicationVideoRecord
    displayName: string | null | undefined
}) {
    const { questionId, videoRecord, displayName } = args
    const gesture = useGesture("application.video", "tutor_application")

    const videoFile =
        Platform.OS === "web"
            ? videoRecord
            : { uri: videoRecord?.uri, name: applicationVideoFileName(displayName), type: "video/mp4" }

    const [globalProgress, submitted, startRequests] = useRequestsBatch(
        [],
        [videoFile],
        "APPLICATION_FORM",
        {
            service: tutorsService.submitTestVideo,
            data: {},
            tmpFilesKey: "video",
            tmpFilesFormatter: (attachmentTmp: string) => attachmentTmp,
            additionalData: [],
            params: { id: questionId },
            onSuccessBeforeNext: () => gesture.succeeded(),
            onError: (error?: unknown) => gesture.failed(error),
        },
        [],
        false
    ) as [number, boolean, () => void]

    return {
        percent: Number(globalProgress ?? 0),
        uploading: !!submitted,
        start: () => {
            gesture.submitted()
            startRequests()
        },
    }
}
